import {
  index,
  integer,
  primaryKey,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import type {
  ComponentBase,
  ComponentKind,
  ComponentPayee,
  Refundability,
  TaxRegime,
  TaxRegistrationScheme,
  TaxRoundingMode,
} from "@/lib/tax/types";
import { hiive } from "./hiive";

export type { TaxSnapshot } from "@/lib/tax/types";

/**
 * A named, reusable stack of taxes and charges.
 *
 * `hiiveId` null = a platform-level template ("18% GST", "UK VAT 20%") that a
 * hiive clones. `regime` is a presentation label — it drives document rules
 * (whether to split 18% GST into 9% CGST + 9% SGST on an intra-state invoice,
 * for instance) and never the arithmetic.
 */
export const taxProfile = sqliteTable(
  "tax_profile",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    hiiveId: integer("hiive_id").references(() => hiive.id, {
      onDelete: "cascade",
    }),
    taxRegistrationId: integer("tax_registration_id").references(
      () => taxRegistration.id,
      { onDelete: "restrict" },
    ),
    name: text("name").notNull(),
    regime: text("regime").notNull().$type<TaxRegime>().default("gst_in"),
    countryCode: text("country_code").notNull().default("IN"),
    roundingMode: text("rounding_mode")
      .notNull()
      .$type<TaxRoundingMode>()
      .default("per_line"),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    ...timestamps,
  },
  (table) => [index("tax_profile_by_hiive").on(table.hiiveId, table.isActive)],
);

/**
 * One entry in a profile's ordered stack.
 *
 * Effective dating lives here rather than on the profile so a rate change
 * supersedes in place, instead of forcing every priced row onto a new profile
 * id and orphaning the ones that were not migrated.
 *
 * `payee`, `minMinor`, `maxMinor` and `refundable` are unexercised while Hiive
 * charges buyers nothing — every component is an organiser tax or charge. They
 * cost one column each now and save a migration if a buyer-facing convenience
 * fee is ever added.
 */
export const taxProfileComponent = sqliteTable(
  "tax_profile_component",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    taxProfileId: integer("tax_profile_id")
      .notNull()
      .references(() => taxProfile.id, { onDelete: "cascade" }),
    kind: text("kind").notNull().$type<ComponentKind>().default("tax"),
    payee: text("payee").notNull().$type<ComponentPayee>().default("organiser"),
    code: text("code").notNull(),
    label: text("label").notNull(),
    rateBps: integer("rate_bps").notNull().default(0),
    fixedMinor: integer("fixed_minor").notNull().default(0),
    minMinor: integer("min_minor"),
    maxMinor: integer("max_minor"),
    appliesTo: text("applies_to")
      .notNull()
      .$type<ComponentBase>()
      .default("net"),
    includedInPrice: integer("included_in_price", { mode: "boolean" })
      .notNull()
      .default(true),
    /** null = whoever is `payee` remits. See PricingComponent.remittedBy. */
    remittedBy: text("remitted_by").$type<ComponentPayee>(),
    refundable: text("refundable")
      .notNull()
      .$type<Refundability>()
      .default("always"),
    /** HSN/SAC for the invoice. Present from day one so the first real tax
     *  invoice does not force a second migration. */
    sacCode: text("sac_code"),
    sortOrder: integer("sort_order").notNull().default(0),
    effectiveFrom: integer("effective_from", { mode: "timestamp" }),
    effectiveUntil: integer("effective_until", { mode: "timestamp" }),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    ...timestamps,
  },
  (table) => [
    index("tax_profile_component_stack").on(
      table.taxProfileId,
      table.sortOrder,
      table.isActive,
    ),
  ],
);

/**
 * The organiser's tax registration — their GSTIN.
 *
 * Load-bearing beyond the invoice: under §32(1) CGST a person who is not
 * registered shall not collect any tax, so the existence of a valid row here is
 * what permits a hiive to attach a tax profile to a price at all. Without it the
 * platform would be rendering an invoice that proves unlawful collection.
 *
 * Generalises `shop_seller_tax_registration` off the seller profile onto the
 * hiive, so events and venues can reach it too.
 */
export const taxRegistration = sqliteTable(
  "tax_registration",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    hiiveId: integer("hiive_id")
      .notNull()
      .references(() => hiive.id, { onDelete: "cascade" }),
    countryCode: text("country_code").notNull().default("IN"),
    subdivisionCode: text("subdivision_code"),
    scheme: text("scheme")
      .notNull()
      .$type<TaxRegistrationScheme>()
      .default("gst"),
    registrationNumber: text("registration_number").notNull(),
    legalName: text("legal_name"),
    /** JSON postal address, printed as the supplier block on the invoice. */
    legalAddress: text("legal_address", { mode: "json" }).$type<{
      line1?: string;
      line2?: string;
      city?: string;
      subdivisionName?: string;
      postalCode?: string;
      countryCode?: string;
    } | null>(),
    validFrom: integer("valid_from", { mode: "timestamp" }),
    validUntil: integer("valid_until", { mode: "timestamp" }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("tax_registration_unique").on(
      table.hiiveId,
      table.countryCode,
      table.scheme,
      table.registrationNumber,
    ),
    index("tax_registration_by_hiive").on(table.hiiveId),
  ],
);

export type TaxProfile = typeof taxProfile.$inferSelect;
export type TaxProfileComponent = typeof taxProfileComponent.$inferSelect;
export type TaxRegistration = typeof taxRegistration.$inferSelect;

/**
 * Sequential document numbers, per series per financial year.
 *
 * A GST invoice serial must be **consecutive** and unique within a financial
 * year. That is a genuinely different rule from the member's booking receipt,
 * whose number is derived from the booking id on purpose (see
 * `bookingInvoiceNumber` — deriving avoids a write path and a contention point
 * for a document that has no such requirement). A commission invoice cannot
 * derive: gaps and reuse are both non-compliant, so it needs a real counter.
 *
 * Allocated with a single atomic upsert (`on conflict do update … returning`),
 * so two concurrent payout runs cannot hand out the same number.
 */
export const COMMISSION_DOCUMENT_KIND = [
  "tax_invoice",
  "bill_of_supply",
] as const;
export type CommissionDocumentKind = (typeof COMMISSION_DOCUMENT_KIND)[number];

export const taxDocumentSequence = sqliteTable(
  "tax_document_sequence",
  {
    /** "commission" today. Room for credit notes, which need their own series. */
    series: text("series").notNull(),
    /** IST financial year label, e.g. "26-27". See lib/tax/financial-year.ts. */
    financialYear: text("financial_year").notNull(),
    nextValue: integer("next_value").notNull().default(1),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      columns: [table.series, table.financialYear],
      name: "tax_document_sequence_pk",
    }),
  ],
);

/**
 * The invoice Hiive issues an organiser for its own platform fee.
 *
 * The second of the two supplies (see plans/tax-system.md §3): the organiser
 * invoices the member for the ticket, and Hiive invoices the organiser for the
 * fee. This document is what lets the organiser claim the GST inside that fee
 * as input tax credit — which matters because the fee is quoted GST-inclusive,
 * so without this row the tax Hiive absorbed is simply lost to them.
 *
 * One row per (party, period). Amounts are snapshotted rather than joined at
 * render time: a payable adjusted after the invoice was issued must not silently
 * restate a tax document that has already been sent.
 */
export const commissionInvoice = sqliteTable(
  "commission_invoice",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    /** GST serial, e.g. "HV/26-27/000123". Unique across all time. */
    invoiceNumber: text("invoice_number").notNull(),
    financialYear: text("financial_year").notNull(),
    sequence: integer("sequence").notNull(),

    payoutPartyId: integer("payout_party_id").notNull(),
    hiiveId: integer("hiive_id").references(() => hiive.id, {
      onDelete: "set null",
    }),

    periodStart: integer("period_start", { mode: "timestamp" }).notNull(),
    periodEnd: integer("period_end", { mode: "timestamp" }).notNull(),
    issuedAt: integer("issued_at", { mode: "timestamp" }).notNull(),

    currency: text("currency").notNull().default("INR"),
    /** The whole deduction the organiser saw on their payout. */
    feeGrossMinor: integer("fee_gross_minor").notNull().default(0),
    /** Hiive's revenue half. */
    feeNetMinor: integer("fee_net_minor").notNull().default(0),
    /** The GST inside the fee — what the organiser claims as ITC. */
    feeTaxMinor: integer("fee_tax_minor").notNull().default(0),
    taxRateBps: integer("tax_rate_bps").notNull().default(0),
    /** How many payables this invoice covers, for the description line. */
    payableCount: integer("payable_count").notNull().default(0),

    /**
     * Which document this is.
     *
     * A registered supplier issues a TAX INVOICE. An unregistered one — which
     * is Hiive today, below the turnover threshold — issues a BILL OF SUPPLY:
     * same sequential numbering, no tax line, no GSTIN. Calling it a tax
     * invoice would be a false claim, and §32(1) CGST forbids an unregistered
     * person from collecting tax at all, so there is nothing to show.
     */
    documentKind: text("document_kind")
      .notNull()
      .$type<CommissionDocumentKind>()
      .default("tax_invoice"),

    /** Hiive's identity at issue time. Snapshotted: a later change of legal
     *  name or address must not rewrite a document already issued. */
    supplierLegalName: text("supplier_legal_name").notNull(),
    /** Null while Hiive is unregistered. */
    supplierGstin: text("supplier_gstin"),
    supplierAddress: text("supplier_address"),
    /** The organiser's identity at issue time. */
    recipientLegalName: text("recipient_legal_name").notNull(),
    recipientGstin: text("recipient_gstin"),

    ...timestamps,
  },
  (table) => [
    uniqueIndex("commission_invoice_number_unique").on(table.invoiceNumber),
    uniqueIndex("commission_invoice_series_unique").on(
      table.financialYear,
      table.sequence,
    ),
    // Idempotency: re-running a payout cycle must find the existing invoice
    // rather than burning another number.
    uniqueIndex("commission_invoice_period_unique").on(
      table.payoutPartyId,
      table.periodStart,
      table.periodEnd,
    ),
    index("commission_invoice_by_hiive").on(table.hiiveId, table.issuedAt),
  ],
);

export type TaxDocumentSequence = typeof taxDocumentSequence.$inferSelect;
export type CommissionInvoice = typeof commissionInvoice.$inferSelect;
